import { useState, useEffect, useRef, forwardRef, useImperativeHandle, type ForwardedRef } from 'react'
import { Progress } from '@/components/ui/progress'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { MultiSelect } from '@/components/ui/multi-select'
import { SingleSelect } from '@/components/ui/single-select'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Heart,
  Target,
  Zap,
  BarChart3,
  PieChart,
  LineChart,
  Activity,
  MessageSquare,
  Mail,
  Phone,
  MoreVertical,
  Loader2,
  Pencil,
  Trash2,
  Play,
  History,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Database,
  ExternalLink,
  Hand,
} from 'lucide-react'
import { useOrganization } from '@/contexts/organization-context'
import { useAuth } from '@/contexts/auth-context'
import { useRoleAccess } from '@/hooks/useRoleAccess'
import { api } from '@/services/api'
import { toast } from 'sonner'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { agentsService, type Agent, type OrganizationMetric, type AgentControl, type SavedReport, type AgentPerformance, type ExecutionHistoryItem, type HandsUpItem } from '@/services/agents'
import { notificationsService } from '@/services/notifications'
import { llmConfigService } from '@/services/llm-config'
import { LLMConfigTab } from '@/components/settings/llm-config-tab'

// Icon mapping for agents
const iconMap: Record<string, any> = {
  "dollar-sign": DollarSign,
  "users": Users,
  "heart": Heart,
  "trending-up": TrendingUp,
  "target": Target,
  "zap": Zap,
  "trending-down": TrendingDown,
  "bar-chart": BarChart3,
  "pie-chart": PieChart,
  "line-chart": LineChart,
  "activity": Activity,
  "message": MessageSquare,
  "mail": Mail,
  "phone": Phone,
  "retention": Heart,
  "monetization": DollarSign,
}

// Color mapping for agents
const colorMap: Record<string, string> = {
  red: '#ef4444',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
  emerald: '#10b981',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  fuchsia: '#d946ef',
  pink: '#ec4899',
  rose: '#f43f5e',
}

// Available icons for selection
const availableIcons = [
  { value: "dollar-sign", component: DollarSign },
  { value: "users", component: Users },
  { value: "heart", component: Heart },
  { value: "trending-up", component: TrendingUp },
  { value: "target", component: Target },
  { value: "zap", component: Zap },
  { value: "trending-down", component: TrendingDown },
  { value: "bar-chart", component: BarChart3 },
  { value: "pie-chart", component: PieChart },
  { value: "line-chart", component: LineChart },
  { value: "activity", component: Activity },
  { value: "message", component: MessageSquare },
  { value: "mail", component: Mail },
  { value: "phone", component: Phone },
]

// Helper to get agent color
const getAgentColor = (agent: Agent): string => {
  if (!agent.color) return '#3b82f6'
  if (agent.color.startsWith('#')) return agent.color
  return colorMap[agent.color] || '#3b82f6'
}

// Analysis frequency <-> cron. The scheduler only reads schedule_cron, so a
// frequency preset is just a friendly wrapper over a cron expression.
const FREQUENCY_OPTIONS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'off', label: 'Off / manual' },
]
const FREQUENCY_TO_CRON: Record<string, string> = {
  hourly: '0 * * * *',      // top of every hour
  daily: '0 9 * * *',       // 09:00
  weekly: '0 9 * * 1',      // Monday 09:00
  monthly: '0 9 1 * *',     // 1st of month 09:00
}
// Reverse-map a stored cron to a frequency preset (tolerant of the time-of-day
// so any daily/weekly/monthly cron maps to the right label). Defaults to daily.
const cronToFrequency = (cron: string | null | undefined): string => {
  if (!cron) return 'daily'
  const parts = cron.trim().split(/\s+/)
  if (parts.length < 5) return 'daily'
  const [, hour, dom, , dow] = parts
  if (hour === '*') return 'hourly'
  if (dom !== '*') return 'monthly'
  if (dow !== '*') return 'weekly'
  return 'daily'
}

export interface AgentsPanelHandle {
  openAddDialog: () => void
  openAIConfig: () => void
}

export interface AgentsPanelProps {
  /** Called when an agent run finishes, so the parent can refresh recommendations. */
  onRunComplete?: () => void
}

function AgentsPanelInner({ onRunComplete }: AgentsPanelProps, ref: ForwardedRef<AgentsPanelHandle>) {
  const { currentOrganization } = useOrganization()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { hasAdminAccess } = useRoleAccess()

  const [agents, setAgents] = useState<Agent[]>([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [agentPerformance, setAgentPerformance] = useState<Record<number, AgentPerformance>>({})
  const [allRecentExecutions, setAllRecentExecutions] = useState<ExecutionHistoryItem[]>([])
  const [handsUpItems, setHandsUpItems] = useState<HandsUpItem[]>([])
  const [clearingHandsUpId, setClearingHandsUpId] = useState<number | null>(null)
  const [executionsLoaded, setExecutionsLoaded] = useState(false)

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    agent_type: "custom",
    color: "#3b82f6",
    icon: "users",
    objective_function: "",
    datasetReportId: null as number | null,
    control: [] as AgentControl[],
    frequency: "daily",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableMetrics, setAvailableMetrics] = useState<OrganizationMetric[]>([])
  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const [isPauseDialogOpen, setIsPauseDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isRunDialogOpen, setIsRunDialogOpen] = useState(false)
  const [agentToRun, setAgentToRun] = useState<Agent | null>(null)
  const [runningAgentId, setRunningAgentId] = useState<number | null>(null)
  // Run progress: event-driven bar fed by the cycle's current_phase.
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null)
  const [, setRunElapsed] = useState(0)  // re-render ticker (drives within-phase creep)
  const [runDone, setRunDone] = useState(false)
  const [runPhase, setRunPhase] = useState<string | null>(null)  // perception | reasoning | action | completed
  const runBaselineIdRef = useRef(0)  // highest execution id before the run, to detect the new one
  const runAgentSlugRef = useRef<string>('')  // slug of the agent being run (to match its execution)
  const runPhaseRef = useRef<string | null>(null)  // last-seen phase (to detect transitions)
  const runPhaseSinceRef = useRef(0)  // when the current phase began (for within-phase creep)
  const [isAIEnabled, setIsAIEnabled] = useState<boolean>(true)  // Track if LLM features are enabled
  const [isAIConfigDialogOpen, setIsAIConfigDialogOpen] = useState(false)

  // Dataset picker: options are saved reports, plus a "None" option (optional dataset)
  const NO_DATASET = "__none__"
  const NEW_REPORT = "__new_report__"
  const datasetOptions = [
    { value: NEW_REPORT, label: "+ New report" },
    { value: NO_DATASET, label: "None (no dataset)" },
    ...savedReports.map(r => ({ value: String(r.id), label: r.name })),
  ]
  const selectedReport = savedReports.find(r => r.id === formData.datasetReportId) || null
  const reportsHref = currentOrganization?.slug ? `/${currentOrganization.slug}/reports` : '/reports'
  const viewReportHref = selectedReport
    ? `${reportsHref}?report=${encodeURIComponent(
        selectedReport.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '').replace(/_+/g, '_')
      )}`
    : reportsHref

  // Fetch agents and their performance
  const fetchAgents = async () => {
    if (!currentOrganization || agentsLoading) {
      if (!currentOrganization) setAgentsLoading(false)
      return
    }

    setAgentsLoading(true)
    try {
      // Fetch agents and performance in parallel
      const [agentsList, performanceData] = await Promise.all([
        agentsService.getAgents(currentOrganization.id),
        agentsService.getAgentsPerformance(currentOrganization.id)
      ])
      setAgents(agentsList)
      setAgentPerformance(performanceData)
    } catch (error: any) {
      console.error('Error fetching agents:', error?.message || error)
      setAgents([])
      setAgentPerformance({})
    } finally {
      setAgentsLoading(false)
    }
  }

  // Load metrics for the Control picker
  const loadMetrics = async (): Promise<void> => {
    setLoadingMetrics(true)
    try {
      const response = await api.get('/data/primitives')
      if (response.data && response.data.primitives) {
        const metrics = response.data.primitives
          .filter((p: any) => p.type === 'metric')
          .map((p: any) => ({
            id: p.slug || p.name,
            description: p.description || p.name
          }))
        setAvailableMetrics(metrics)
      }
    } catch (error: any) {
      console.error('Error loading metrics:', error)
      setAvailableMetrics([])
    } finally {
      setLoadingMetrics(false)
    }
  }

  // Load saved reports for the Dataset picker
  const loadSavedReports = async () => {
    try {
      const reports = await agentsService.getSavedReports()
      setSavedReports([...reports].sort((a, b) => a.name.localeCompare(b.name)))
    } catch (error) {
      console.error('Error loading saved reports:', error)
      setSavedReports([])
    }
  }

  // While the edit dialog is open, refresh the saved reports list whenever the
  // user returns to this tab. "+ New report" opens the Reports page in a
  // separate tab, so refetching on focus/visibility makes a freshly created
  // report appear in the Dataset picker without a full page reload.
  useEffect(() => {
    if (!isEditDialogOpen) return

    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadSavedReports()
      }
    }

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [isEditDialogOpen])

  // Open edit dialog
  const openEditDialog = async (agent: Agent) => {
    setSelectedAgent(agent)

    setFormData({
      name: agent.name,
      description: agent.description || "",
      agent_type: agent.agent_type || "custom",
      color: agent.color || "#3b82f6",
      icon: agent.icon || "users",
      objective_function: agent.objective_function || "",
      datasetReportId: agent.dataset_report_id ?? null,
      control: agent.control || [],
      frequency: agent.schedule_enabled === false ? "off" : cronToFrequency(agent.schedule_cron),
    })

    // Open dialog immediately, then load metrics in background
    setIsEditDialogOpen(true)

    // Refresh the reports list so the Dataset picker reflects any reports
    // created since the panel first mounted.
    loadSavedReports()

    await loadMetrics()
  }

  // Open add dialog (new agent)
  const openAddDialog = () => {
    setSelectedAgent(null)
    setFormData({
      name: "",
      description: "",
      agent_type: "custom",
      color: "#3b82f6",
      icon: "users",
      objective_function: "",
      datasetReportId: null,
      control: [],
      frequency: "daily",
    })
    loadMetrics()
    setIsEditDialogOpen(true)
  }

  // Handle add agent - Note: In SDK-First architecture, agents are registered via SDK
  // This is kept for backwards compatibility but may be deprecated
  const handleAddAgent = async () => {
    if (!currentOrganization?.id) return

    setIsSubmitting(true)
    try {
      const isOff = formData.frequency === "off"
      const desiredCron = isOff ? undefined : FREQUENCY_TO_CRON[formData.frequency]
      const newAgent = await agentsService.createAgent(currentOrganization.id, {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        objective_function: formData.objective_function,
        dataset_report_id: formData.datasetReportId,
        control: formData.control.length > 0 ? formData.control : undefined,
        schedule_cron: desiredCron,
      })

      // Ensure the schedule is applied via a follow-up update (which reliably
      // persists it). Create defaults schedule_enabled=true, so "off" needs it.
      let finalAgent = newAgent
      try {
        if (isOff) {
          finalAgent = await agentsService.updateAgent(newAgent.id, currentOrganization.id, {
            schedule_enabled: false,
          })
        } else if (newAgent.schedule_cron !== desiredCron) {
          finalAgent = await agentsService.updateAgent(newAgent.id, currentOrganization.id, {
            schedule_cron: desiredCron,
            schedule_enabled: true,
          })
        }
      } catch (e) {
        console.error('Failed to set agent schedule after create:', e)
      }

      setAgents([finalAgent, ...agents])
      setIsEditDialogOpen(false)
      setFormData({ name: "", description: "", agent_type: "custom", color: "#3b82f6", icon: "users", objective_function: "", datasetReportId: null, control: [], frequency: "daily" })
      toast.success(`Agent "${newAgent.name}" created successfully!`)
    } catch (error: any) {
      console.error('Error creating agent:', error)
      toast.error(`Failed to create agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle edit agent
  const handleEditAgent = async () => {
    if (!selectedAgent || !currentOrganization?.id) return

    setIsSubmitting(true)
    try {
      const isOff = formData.frequency === "off"
      const updatedAgent = await agentsService.updateAgent(
        selectedAgent.id,
        currentOrganization.id,
        {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          icon: formData.icon,
          objective_function: formData.objective_function,
          dataset_report_id: formData.datasetReportId,
          control: formData.control,
          // Off = keep the cron but disable auto-runs (manual only).
          schedule_enabled: !isOff,
          ...(isOff ? {} : { schedule_cron: FREQUENCY_TO_CRON[formData.frequency] }),
        }
      )

      setAgents(agents.map((agent) =>
        agent.id === selectedAgent.id ? updatedAgent : agent
      ))

      setIsEditDialogOpen(false)
      setSelectedAgent(null)
      setFormData({ name: "", description: "", agent_type: "custom", color: "#3b82f6", icon: "users", objective_function: "", datasetReportId: null, control: [], frequency: "daily" })
      toast.success("Agent updated successfully!")
    } catch (error: any) {
      console.error('Error updating agent:', error)
      const errorMessage = error.response?.data?.message
        || error.response?.data?.details?.original_error
        || error.response?.data?.detail
        || error.message
      toast.error(`Failed to update agent: ${errorMessage}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle pause agent
  const handlePauseAgent = async () => {
    if (!selectedAgent) return

    setIsSubmitting(true)
    try {
      const updatedAgent = await agentsService.pauseAgent(selectedAgent.id)
      setAgents(agents.map((a) => a.id === selectedAgent.id ? updatedAgent : a))
      setSelectedAgent(updatedAgent)
      setIsPauseDialogOpen(false)
      toast.success(`Agent "${selectedAgent.name}" paused!`)
    } catch (error: any) {
      console.error('Error pausing agent:', error)
      toast.error(`Failed to pause agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle activate agent
  const handleActivateAgent = async () => {
    if (!selectedAgent) return

    setIsSubmitting(true)
    try {
      const updatedAgent = await agentsService.activateAgent(selectedAgent.id)
      setAgents(agents.map((a) => a.id === selectedAgent.id ? updatedAgent : a))
      setSelectedAgent(updatedAgent)
      toast.success(`Agent "${selectedAgent.name}" activated!`)
    } catch (error: any) {
      console.error('Error activating agent:', error)
      toast.error(`Failed to activate agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle delete agent
  const handleDeleteAgent = async () => {
    if (!selectedAgent) return

    setIsSubmitting(true)
    try {
      await agentsService.deleteAgent(selectedAgent.id)
      setAgents(agents.filter((agent) => agent.id !== selectedAgent.id))
      setIsDeleteDialogOpen(false)
      setIsEditDialogOpen(false)
      toast.success(`Agent "${selectedAgent.name}" deleted successfully!`)
      setSelectedAgent(null)
    } catch (error: any) {
      console.error('Error deleting agent:', error)
      toast.error(`Failed to delete agent: ${error.response?.data?.detail || error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle run agent analysis
  const handleRunAgent = async (agent: Agent) => {
    if (!currentOrganization?.id) {
      toast.error('No organization selected')
      return
    }
    const agentSlug = agent.slug || `platform-agent-${agent.id}`
    // Baseline the latest execution id for THIS agent so we can detect the new run.
    try {
      const monitoring = await agentsService.getOptimizerMonitoring()
      runBaselineIdRef.current = (monitoring.recent_executions || [])
        .filter(e => e.agent_id === agentSlug)
        .reduce((m, e) => Math.max(m, e.id), 0)
    } catch {
      runBaselineIdRef.current = 0
    }
    runAgentSlugRef.current = agentSlug
    runPhaseRef.current = 'perception'
    runPhaseSinceRef.current = Date.now()
    setRunPhase('perception')
    setRunningAgentId(agent.id)
    setRunStartedAt(Date.now())
    setRunElapsed(0)
    setRunDone(false)
    try {
      await agentsService.runAgent(agent.id, currentOrganization.id)
      // Keep the bar running; phase + completion come from the poll effect below.
    } catch (error: any) {
      console.error('Error running agent:', error)
      toast.error(`Failed to run agent: ${error.response?.data?.detail || error.message}`)
      setRunningAgentId(null)
      setRunStartedAt(null)
      setRunPhase(null)
    }
  }

  const RUN_TIMEOUT_MS = 180000  // safety net if we never see a terminal status

  // Re-render ticker so the within-phase creep animates between phase events.
  useEffect(() => {
    if (runningAgentId === null || runStartedAt === null || runDone) return
    const t = setInterval(() => setRunElapsed(Date.now() - runStartedAt), 250)
    return () => clearInterval(t)
  }, [runningAgentId, runStartedAt, runDone])

  // Poll monitoring for this run's execution: advance the phase, finish on a
  // terminal status, and keep the executions list live-refreshed meanwhile.
  useEffect(() => {
    if (runningAgentId === null || runStartedAt === null || runDone) return
    const slug = runAgentSlugRef.current
    const t = setInterval(async () => {
      if (Date.now() - runStartedAt > RUN_TIMEOUT_MS) {
        setRunDone(true)
        return
      }
      try {
        const monitoring = await agentsService.getOptimizerMonitoring()
        // Live-refresh the lists during the run (no page reload needed).
        setAllRecentExecutions(monitoring.recent_executions || [])
        setHandsUpItems(monitoring.pending_hands_up || [])

        const current = (monitoring.recent_executions || [])
          .filter(e => e.agent_id === slug && e.id > runBaselineIdRef.current)
          .sort((a, b) => b.id - a.id)[0]
        if (!current) return

        if (current.status === 'completed' || current.status === 'failed') {
          setRunDone(true)
          if (current.status === 'failed') toast.error('Agent run failed')
          return
        }
        // Still running: advance the phase (jump the bar forward on transitions).
        const phase = current.current_phase || 'perception'
        if (runPhaseRef.current !== phase) {
          runPhaseRef.current = phase
          runPhaseSinceRef.current = Date.now()
          setRunPhase(phase)
        }
      } catch {
        // keep polling; the timeout resolves it if monitoring is unavailable
      }
    }, 3000)
    return () => clearInterval(t)
  }, [runningAgentId, runStartedAt, runDone])

  // Once done, refresh the lists + the parent's recommendations, hold the
  // completed bar briefly, then clear it.
  useEffect(() => {
    if (!runDone) return
    agentsService.getOptimizerMonitoring()
      .then(monitoring => {
        setAllRecentExecutions(monitoring.recent_executions || [])
        setHandsUpItems(monitoring.pending_hands_up || [])
      })
      .catch(() => { /* leave the list as-is if the refresh fails */ })
    onRunComplete?.()  // refresh the recommendations list on the parent page
    const t = setTimeout(() => {
      setRunningAgentId(null)
      setRunStartedAt(null)
      setRunElapsed(0)
      setRunDone(false)
      setRunPhase(null)
      runPhaseRef.current = null
    }, 2000)
    return () => clearTimeout(t)
  }, [runDone])

  // Map the current phase to a bar value. Each phase owns a segment; within a
  // segment the bar eases toward (but never reaches) the next boundary, so it
  // never looks frozen, and a real phase event visibly jumps it forward.
  const PHASE_SEGMENTS: Record<string, [number, number]> = {
    perception: [4, 30],
    reasoning: [30, 70],
    action: [70, 90],
  }
  const runProgressValue = (): number => {
    if (runDone || runPhase === 'completed') return 100
    const [start, end] = PHASE_SEGMENTS[runPhase || 'perception'] || PHASE_SEGMENTS.perception
    const timeInPhase = Date.now() - runPhaseSinceRef.current
    return start + (end - 2 - start) * (1 - Math.exp(-timeInPhase / 10000))
  }

  // Fetch LLM config to check if AI features are enabled
  const fetchLLMConfig = async () => {
    if (!currentOrganization?.id) return
    try {
      const config = await llmConfigService.getConfig(currentOrganization.id)
      setIsAIEnabled(config.is_active)
    } catch (error) {
      console.error('Error fetching LLM config:', error)
      // Default to true if we can't fetch config
      setIsAIEnabled(true)
    }
  }

  // Fetch recent executions + pending hands-up from the optimizer monitoring endpoint (once)
  const fetchAllRecentExecutions = async () => {
    if (executionsLoaded) return
    try {
      const monitoring = await agentsService.getOptimizerMonitoring()
      setAllRecentExecutions(monitoring.recent_executions || [])
      setHandsUpItems(monitoring.pending_hands_up || [])
      setExecutionsLoaded(true)
    } catch (error) {
      console.error('Error fetching monitoring data:', error)
      setAllRecentExecutions([])
      setHandsUpItems([])
      setExecutionsLoaded(true)
    }
  }

  // Pending hands-up escalations for a specific agent (drives the raised-hand icon).
  const getAgentHandsUp = (agent: Agent): HandsUpItem[] => {
    const agentSlug = agent.slug || `platform-agent-${agent.id}`
    return handsUpItems.filter(h => h.agent_id === agentSlug && h.status === 'pending')
  }

  // Clear (dismiss) a hands-up escalation so the agent can run again.
  const handleClearHandsUp = async (handsUp: HandsUpItem) => {
    setClearingHandsUpId(handsUp.id)
    try {
      await notificationsService.resolveHandsUp(
        0, // agent id is unused by the endpoint
        handsUp.id,
        { status: 'rejected' }, // rejected -> dismissed
        user?.email || 'unknown'
      )
      // Refresh monitoring so the raised-hand icon and lists update immediately.
      const monitoring = await agentsService.getOptimizerMonitoring()
      setHandsUpItems(monitoring.pending_hands_up || [])
      setAllRecentExecutions(monitoring.recent_executions || [])
      toast.success('Hands-up cleared — the agent can run again.')
    } catch (error: any) {
      console.error('Error clearing hands-up:', error)
      toast.error(`Failed to clear: ${error.response?.data?.detail || error.message}`)
    } finally {
      setClearingHandsUpId(null)
    }
  }

  // Get executions for a specific agent from the cached monitoring data
  const getAgentExecutions = (agent: Agent): ExecutionHistoryItem[] => {
    // Filter by agent slug (agent_id in ExecutionHistoryItem is the slug)
    const agentSlug = agent.slug || `platform-agent-${agent.id}`
    return allRecentExecutions
      .filter(exec => exec.agent_id === agentSlug)
      .slice(0, 7)  // Limit to 7 most recent
  }

  // Trigger fetch when submenu opens
  const handleExecutionsSubMenuOpen = (open: boolean) => {
    if (open && !executionsLoaded) {
      fetchAllRecentExecutions()
    }
  }

  // Get status icon for execution
  const getExecutionStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <CheckCircle2 className="h-3 w-3 text-green-500" />
      case 'failed':
      case 'error':
        return <XCircle className="h-3 w-3 text-red-500" />
      case 'running':
      case 'in_progress':
        return <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
      case 'pending':
      case 'queued':
        return <Clock className="h-3 w-3 text-yellow-500" />
      default:
        return <AlertCircle className="h-3 w-3 text-stone-400" />
    }
  }

  // Format execution date for display
  const formatExecutionDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Fuller date+time for the hands-up popup (e.g. "Jul 3, 2026, 9:51 PM")
  const formatHandsUpDate = (dateStr: string | null): string | null => {
    if (!dateStr) return null
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    })
  }

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchAgents()
      fetchLLMConfig()
      loadSavedReports()
      fetchAllRecentExecutions()  // loads recent executions + pending hands-up for the icons
    }
  }, [currentOrganization?.id])

  // Expose the header actions so the page (Agents title row) can trigger them.
  useImperativeHandle(ref, () => ({
    openAddDialog,
    openAIConfig: () => setIsAIConfigDialogOpen(true),
  }))

  return (
    <>
      {/* Agents Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            {agentsLoading ? (
              // Loading skeletons
              Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="flex-1 min-w-[140px]">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-8 rounded-lg" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-24 mb-2" />
                    <Skeleton className="h-4 w-32" />
                  </CardContent>
                </Card>
              ))
            ) : agents.length === 0 ? (
              <Card className="w-full">
                <CardContent className="py-8 text-center">
                  <p className="text-sm text-stone-500">No active agents found</p>
                </CardContent>
              </Card>
            ) : (
              agents.map((agent) => {
            const iconKey = (agent.icon?.trim() || "users") as keyof typeof iconMap
            const IconComponent = iconMap[iconKey] || Users
            const bgColor = getAgentColor(agent)
            const isActive = agent.status === 'active'

            // Get real performance data for this agent
            const performance = agentPerformance[agent.id]
            const effectiveness = performance?.effectiveness
            const effectivenessPercent = effectiveness !== null && effectiveness !== undefined
              ? Math.round(effectiveness * 100)
              : null
            const trend = performance?.effectiveness_trend || 'unknown'

            // Stone color for effectiveness (consistent with theme)
            const getEffectivenessColor = (value: number | null) => {
              if (value === null) return 'text-stone-400'
              return 'text-stone-700'
            }

            // Trend icon and change value (stone theme)
            const getTrendIcon = (t: string) => {
              switch (t) {
                case 'improving':
                  return { icon: TrendingUp, color: 'text-stone-600' }
                case 'declining':
                  return { icon: TrendingDown, color: 'text-stone-500' }
                case 'stable':
                  return { icon: TrendingUp, color: 'text-stone-400', rotate: true }
                default:
                  return { icon: null, color: 'text-stone-400' }
              }
            }
            const trendDisplay = getTrendIcon(trend)
            const TrendIcon = trendDisplay.icon

            // Get change percentage from API (converted to percentage points)
            const changePercent = performance?.effectiveness_change !== null && performance?.effectiveness_change !== undefined
              ? performance.effectiveness_change * 100
              : null

            return (
              <Card key={agent.id} className="relative group flex-1 min-w-[140px] overflow-hidden">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <CardTitle className={`text-lg font-semibold truncate ${isActive ? 'text-stone-700' : 'text-stone-400'}`}>{agent.name}</CardTitle>
                    {/* Raised-hand indicator: click for the escalation details + clear */}
                    {getAgentHandsUp(agent).length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="shrink-0 text-amber-500 animate-pulse hover:text-amber-600 transition-colors"
                            title="Needs attention — click for details"
                          >
                            <Hand className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 p-0">
                          <div className="p-3 border-b">
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-stone-700">
                              <Hand className="h-4 w-4 text-amber-500" />
                              Needs attention
                            </div>
                            <p className="text-xs text-stone-500 mt-0.5">
                              This agent won't run until the escalation is cleared.
                            </p>
                          </div>
                          <div className="max-h-72 overflow-y-auto divide-y">
                            {getAgentHandsUp(agent).map(h => (
                              <div key={h.id} className="p-3 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-stone-600">
                                    {[
                                      h.cycle_number != null ? `Cycle #${h.cycle_number}` : null,
                                      formatHandsUpDate(h.created_at),
                                    ].filter(Boolean).join(' · ')}
                                  </span>
                                  {h.category && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                      {h.category}
                                    </span>
                                  )}
                                </div>
                                {h.reason && (
                                  <p className="text-xs text-stone-600 whitespace-pre-wrap break-words">
                                    {h.reason}
                                  </p>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full h-7 text-xs"
                                  disabled={clearingHandsUpId === h.id}
                                  onClick={() => handleClearHandsUp(h)}
                                >
                                  {clearingHandsUpId === h.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : (
                                    <XCircle className="h-3 w-3 mr-1" />
                                  )}
                                  Clear
                                </Button>
                              </div>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    {/* Agent icon doubles as the actions menu trigger (hover reveals ⋮, click opens) */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`order-first relative h-8 w-8 shrink-0 rounded-lg flex items-center justify-center transition group/icon hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-stone-300 ${!isActive ? 'opacity-40 grayscale' : ''}`}
                          style={{ backgroundColor: bgColor }}
                          title="Agent actions"
                        >
                          <IconComponent className="h-4 w-4 text-white transition-opacity group-hover/icon:opacity-0" />
                          <MoreVertical className="h-4 w-4 text-white absolute opacity-0 transition-opacity group-hover/icon:opacity-100" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44">
                        <DropdownMenuItem onClick={() => openEditDialog(agent)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setAgentToRun(agent)
                            setIsRunDialogOpen(true)
                          }}
                          disabled={agent.status !== 'active' || runningAgentId === agent.id || getAgentHandsUp(agent).length > 0}
                        >
                          <Play className="h-4 w-4 mr-2" />
                          Run
                        </DropdownMenuItem>
                        <DropdownMenuSub onOpenChange={handleExecutionsSubMenuOpen}>
                          <DropdownMenuSubTrigger>
                            <History className="h-4 w-4 mr-2" />
                            Executions
                            <ChevronRight className="h-4 w-4 ml-auto" />
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-56">
                            {!executionsLoaded ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-stone-400" />
                              </div>
                            ) : getAgentExecutions(agent).length === 0 ? (
                              <div className="text-sm text-stone-400 text-center py-4">
                                No recent executions
                              </div>
                            ) : (
                              getAgentExecutions(agent).map((execution) => (
                                <DropdownMenuItem
                                  key={execution.id}
                                  onClick={() => navigate(`/${currentOrganization?.slug}/agents/${agent.id}`)}
                                  className="flex items-center justify-between gap-2 text-sm"
                                >
                                  <span className="text-stone-500">
                                    {formatExecutionDate(execution.started_at || execution.completed_at)}
                                  </span>
                                  <span className="font-mono text-stone-600">
                                    #{execution.cycle_number}
                                  </span>
                                  {getExecutionStatusIcon(execution.status)}
                                </DropdownMenuItem>
                              ))
                            )}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  {effectivenessPercent !== null ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-default">
                            <div className={`text-xl font-bold ${getEffectivenessColor(effectivenessPercent)}`}>
                              {effectivenessPercent}%
                            </div>
                            <div className="flex items-center text-xs text-stone-500">
                              {TrendIcon ? (
                                <TrendIcon
                                  className={`mr-1 h-3 w-3 ${trendDisplay.color} ${trendDisplay.rotate ? 'rotate-90' : ''}`}
                                />
                              ) : (
                                <TrendingUp className="mr-1 h-3 w-3 text-stone-400 rotate-90" />
                              )}
                              <span className={trendDisplay.color}>
                                {changePercent !== null
                                  ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(1)}%`
                                  : '0.0%'
                                }
                              </span>
                              <span className="ml-1">from last period</span>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Effectiveness: actual/subgoal</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <div className="text-sm text-stone-400">No data</div>
                  )}

                  {/* Run progress: event-driven bar fed by the cycle's current_phase */}
                  {runningAgentId === agent.id && runStartedAt !== null && (
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center justify-between text-[11px] text-stone-500">
                        <span className="flex items-center gap-1">
                          {!runDone && runPhase !== 'completed' && (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          )}
                          {runDone
                            ? 'Analysis complete'
                            : runPhase === 'reasoning'
                            ? 'Analyzing data…'
                            : runPhase === 'action'
                            ? 'Preparing recommendations…'
                            : 'Reading dataset…'}
                        </span>
                        <span className="font-mono">{Math.round(runProgressValue())}%</span>
                      </div>
                      <Progress
                        value={runProgressValue()}
                        className="h-1.5 transition-all duration-500"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })
        )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Agent Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden" aria-describedby={undefined}>
          <DialogTitle className="sr-only">{selectedAgent ? `Edit ${selectedAgent.name}` : 'New Agent'}</DialogTitle>
          <div className="flex flex-col h-full">
            {/* Header with Icon, Name, Description */}
            <div className="p-6 pb-4 border-b">
              <div className="flex items-start gap-4">
                {/* Clickable Icon - opens appearance popover */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="shrink-0 h-16 w-16 rounded-xl flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      style={{ backgroundColor: formData.color.startsWith('#') ? formData.color : '#3b82f6' }}
                    >
                      {(() => {
                        const iconKey = (formData.icon?.trim() || "users") as keyof typeof iconMap
                        const IconComponent = iconMap[iconKey] || Users
                        return <IconComponent className="h-8 w-8 text-white" />
                      })()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64 p-4">
                    <div className="space-y-4">
                      <div className="text-sm font-medium text-slate-700">Appearance</div>
                      <div className="flex items-center gap-3">
                        <div
                          className="h-12 w-12 rounded-lg border-2 border-slate-200 overflow-hidden"
                          style={{ backgroundColor: formData.color.startsWith('#') ? formData.color : '#3b82f6' }}
                        />
                        <input
                          type="color"
                          value={formData.color.startsWith('#') ? formData.color : '#3b82f6'}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                          className="h-8 w-16 rounded border border-slate-200 cursor-pointer"
                        />
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {availableIcons.map((icon) => {
                          const IconComp = icon.component
                          return (
                            <button
                              key={icon.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, icon: icon.value })}
                              className={`h-9 w-9 rounded-lg border-2 flex items-center justify-center transition-all ${
                                formData.icon === icon.value
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-slate-200 hover:border-slate-300 bg-white'
                              }`}
                            >
                              <IconComp className="h-4 w-4 text-slate-600" />
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Editable Name and Description */}
                <div className="flex-1 space-y-1">
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Agent Name"
                    className="text-2xl font-semibold border-0 p-0 h-auto focus-visible:ring-0 placeholder:text-slate-300"
                  />
                  <div className="flex items-center justify-between">
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Agent description..."
                      className="flex-1 text-sm text-slate-500 border-0 p-0 h-auto focus-visible:ring-0 placeholder:text-slate-300"
                    />
                    {selectedAgent && (
                      <div className="flex items-center gap-2 ml-4">
                        <Label htmlFor="agent-active" className="text-xs text-slate-500">Active</Label>
                        <Switch
                          id="agent-active"
                          checked={selectedAgent.status === 'active'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleActivateAgent()
                            } else {
                              handlePauseAgent()
                            }
                          }}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content - Prompt Textarea */}
            <div className="flex-1 p-6 overflow-y-auto">
              <h3 className="text-lg font-semibold text-slate-900">
                {isAIEnabled ? 'Objective Function' : 'Decision Rules'}
              </h3>
              <p className="text-sm text-slate-500 mb-3">
                {isAIEnabled
                  ? 'This prompt will be sent to the AI every time the agent runs, along with your data context.'
                  : 'Define the rules and thresholds the analytical reasoner will use to make decisions. The system will apply statistical analysis and formulas based on these rules.'}
              </p>
              <div className={`rounded-xl border overflow-hidden ${isAIEnabled ? 'border-blue-200 bg-blue-50/30' : 'border-stone-200 bg-stone-50/30'}`}>
                <Textarea
                  id="edit-objective_function"
                  placeholder={isAIEnabled
                    ? `Tell the AI what to do...

Example: You are a budget optimization agent. Analyze campaign performance data daily and:
- Identify campaigns with ROAS > 2.0 that could benefit from increased budget
- Flag campaigns with declining performance for review
- Recommend budget shifts from underperformers to top performers
- Always explain your reasoning and confidence level`
                    : `Define decision rules and thresholds...

Example: This agent optimizes campaign budgets using the following rules:
- IF ROAS > 2.0 AND spend < $10K THEN recommend budget increase of 20%
- IF ROAS < 1.0 for 7+ consecutive days THEN flag for review
- IF CPI > $3.50 THEN recommend bid reduction of 15%
- Reallocate budget from bottom 20% performers to top 20%`}
                  value={formData.objective_function}
                  onChange={(e) => setFormData({ ...formData, objective_function: e.target.value })}
                  className="min-h-[280px] border-0 bg-transparent focus-visible:ring-0 resize-none text-sm p-4 placeholder:text-slate-400"
                />
              </div>
            </div>

            {/* Footer - Dataset, Control, Actions */}
            <div className="p-6 pt-4 border-t bg-slate-50/50">
              <div className="flex items-start gap-6">
                {/* Dataset (bound saved report) */}
                <div className="flex-1 space-y-2">
                  <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                    <Database className="h-3 w-3" />
                    Dataset
                  </Label>
                  <SingleSelect
                    options={datasetOptions}
                    value={formData.datasetReportId != null ? String(formData.datasetReportId) : NO_DATASET}
                    onValueChange={(value) => {
                      if (value === NEW_REPORT) {
                        window.open(reportsHref, '_blank', 'noopener,noreferrer')
                        return
                      }
                      setFormData({ ...formData, datasetReportId: value === NO_DATASET ? null : Number(value) })
                    }}
                    placeholder="Select a report..."
                    searchPlaceholder="Search reports..."
                    emptyText="No saved reports found."
                  />
                  {selectedReport && (
                    <a
                      href={viewReportHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View report
                    </a>
                  )}
                </div>

                {/* Control (target metrics) */}
                <div className="flex-1 space-y-2">
                  <Label
                    className="text-xs font-medium text-slate-600 flex items-center gap-1.5 cursor-help"
                    title="Select the metrics this agent is responsible for optimizing"
                  >
                    <Target className="h-3 w-3" />
                    Control
                  </Label>
                  {loadingMetrics ? (
                    <div className="flex items-center gap-2 text-xs text-slate-500 p-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading...
                    </div>
                  ) : availableMetrics.length === 0 ? (
                    <div className="text-xs text-slate-400 p-2 bg-slate-100 rounded">
                      No metrics available. Configure metrics in your organization settings.
                    </div>
                  ) : (
                    <MultiSelect
                      options={availableMetrics.map(m => ({ value: m.id, label: m.description || m.id }))}
                      value={formData.control.map(o => o.name)}
                      onValueChange={(value) => setFormData({ ...formData, control: value.map(v => ({ name: v })) })}
                      placeholder="Select target metrics..."
                      searchPlaceholder="Search metrics..."
                      emptyText="No metrics found."
                    />
                  )}
                </div>

                {/* Frequency (how often the agent runs its analysis) */}
                <div className="w-44 space-y-2">
                  <Label
                    className="text-xs font-medium text-slate-600 flex items-center gap-1.5 cursor-help"
                    title="How often this agent automatically runs its analysis"
                  >
                    <Clock className="h-3 w-3" />
                    Frequency
                  </Label>
                  <SingleSelect
                    options={FREQUENCY_OPTIONS}
                    value={formData.frequency}
                    onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                    placeholder="Select frequency..."
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  {selectedAgent && (
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setIsDeleteDialogOpen(true)} disabled={isSubmitting}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {hasAdminAccess() && selectedAgent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleRunAgent(selectedAgent)
                        setIsEditDialogOpen(false)
                      }}
                      disabled={runningAgentId === selectedAgent.id || selectedAgent.status !== 'active' || isSubmitting || getAgentHandsUp(selectedAgent).length > 0}
                    >
                      {runningAgentId === selectedAgent.id ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      Run Analysis
                    </Button>
                  )}
                  <Button size="sm" onClick={selectedAgent ? handleEditAgent : handleAddAgent} disabled={!formData.name || isSubmitting}>
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (selectedAgent ? 'Save' : 'Create')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pause Confirmation Dialog */}
      <Dialog open={isPauseDialogOpen} onOpenChange={setIsPauseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pause Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to pause <span className="font-semibold text-stone-900">"{selectedAgent?.name}"</span>?
              The agent will stop running until you activate it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPauseDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handlePauseAgent} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Pause Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Agent</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-stone-900">"{selectedAgent?.name}"</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAgent} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete Agent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Agent Confirmation Dialog */}
      <Dialog open={isRunDialogOpen} onOpenChange={setIsRunDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run Agent Analysis</DialogTitle>
            <DialogDescription>
              Are you sure you want to run <span className="font-semibold text-stone-900">"{agentToRun?.name}"</span>?
              This will trigger a new analysis cycle and may take a few minutes to complete.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRunDialogOpen(false)} disabled={runningAgentId === agentToRun?.id}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (agentToRun) {
                  setIsRunDialogOpen(false)
                  await handleRunAgent(agentToRun)
                  setAgentToRun(null)
                }
              }}
              disabled={runningAgentId === agentToRun?.id || (agentToRun ? getAgentHandsUp(agentToRun).length > 0 : false)}
            >
              {runningAgentId === agentToRun?.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  Run Analysis
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Configuration Dialog */}
      <Dialog open={isAIConfigDialogOpen} onOpenChange={setIsAIConfigDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Configuration</DialogTitle>
            <DialogDescription>
              Configure API keys and budget settings for AI-powered agents
            </DialogDescription>
          </DialogHeader>
          {currentOrganization?.id && (
            <LLMConfigTab organizationId={currentOrganization.id} />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export const AgentsPanel = forwardRef<AgentsPanelHandle, AgentsPanelProps>(AgentsPanelInner)
